<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Household Map View</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body p-0">
          <div class="row g-0">
            <!-- Map Container -->
            <div class="col-lg-8">
              <div id="household-map" style="height: 600px; width: 100%;"></div>
            </div>

            <!-- Sidebar -->
            <div class="col-lg-4 border-start">
              <div class="p-3" style="height: 600px; overflow-y: auto;">
                <!-- Map Controls -->
                <div class="mb-4">
                  <h6 class="mb-3">Map Filters</h6>

                  <!-- Filter by Status -->
                  <div class="mb-3">
                    <label class="form-label">Household Type</label>
                    <select v-model="filterType" @change="applyFilters" class="form-select form-select-sm">
                      <option value="all">All Households</option>
                      <option value="cbhi">CBHI Members Only</option>
                      <option value="non-cbhi">Non-CBHI Only</option>
                      <option value="model">Model Households</option>
                      <option value="vulnerable">Vulnerable Households</option>
                    </select>
                  </div>

                  <!-- Filter by Visit Status -->
                  <div class="mb-3">
                    <label class="form-label">Visit Status</label>
                    <select v-model="filterVisit" @change="applyFilters" class="form-select form-select-sm">
                      <option value="all">All</option>
                      <option value="never">Never Visited</option>
                      <option value="overdue">Overdue (>30 days)</option>
                      <option value="recent">Recently Visited</option>
                    </select>
                  </div>

                  <!-- Legend -->
                  <div class="card bg-light">
                    <div class="card-body p-2">
                      <h6 class="card-title mb-2">Legend</h6>
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge bg-success me-2" style="width: 20px; height: 20px;"></span>
                        <small>CBHI Active</small>
                      </div>
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge bg-secondary me-2" style="width: 20px; height: 20px;"></span>
                        <small>Non-CBHI</small>
                      </div>
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge bg-warning me-2" style="width: 20px; height: 20px;"></span>
                        <small>Model Household</small>
                      </div>
                      <div class="d-flex align-items-center">
                        <span class="badge bg-danger me-2" style="width: 20px; height: 20px;"></span>
                        <small>Needs Visit</small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Statistics -->
                <div class="mb-4">
                  <h6 class="mb-3">Map Statistics</h6>
                  <div class="row g-2">
                    <div class="col-6">
                      <div class="card text-center">
                        <div class="card-body p-2">
                          <h4 class="mb-0">{{ visibleHouseholds.length }}</h4>
                          <small class="text-muted">Visible</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="card text-center">
                        <div class="card-body p-2">
                          <h4 class="mb-0 text-success">{{ cbhiCount }}</h4>
                          <small class="text-muted">CBHI</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="card text-center">
                        <div class="card-body p-2">
                          <h4 class="mb-0 text-warning">{{ modelCount }}</h4>
                          <small class="text-muted">Model</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="card text-center">
                        <div class="card-body p-2">
                          <h4 class="mb-0 text-danger">{{ overdueCount }}</h4>
                          <small class="text-muted">Overdue</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Selected Household -->
                <div v-if="selectedHousehold" class="mb-4">
                  <h6 class="mb-3">Selected Household</h6>
                  <div class="card border-primary">
                    <div class="card-body">
                      <h5 class="card-title">{{ selectedHousehold.household_number }}</h5>
                      <p class="mb-2">
                        <strong>Head:</strong> {{ selectedHousehold.household_head_name || 'Not assigned' }}
                      </p>
                      <p class="mb-2">
                        <strong>Members:</strong> {{ selectedHousehold.member_count || 0 }}
                        <span v-if="selectedHousehold.cbhi_member_count > 0" class="text-success">
                          ({{ selectedHousehold.cbhi_member_count }} CBHI)
                        </span>
                      </p>
                      <p class="mb-2">
                        <strong>Last Visit:</strong>
                        <span v-if="selectedHousehold.last_visit_date">
                          {{ formatDate(selectedHousehold.last_visit_date) }}
                        </span>
                        <span v-else class="text-muted">Never visited</span>
                      </p>
                      <div class="mb-2">
                        <span v-if="selectedHousehold.is_model_household" class="badge bg-warning me-1">
                          Model Household
                        </span>
                        <span v-if="selectedHousehold.is_vulnerable" class="badge bg-danger">
                          Vulnerable
                        </span>
                      </div>
                      <div class="d-grid gap-2 mt-3">
                        <button @click="viewHouseholdDetails" class="btn btn-primary btn-sm">
                          <i class="bx bx-show"></i> View Details
                        </button>
                        <button @click="startVisit" class="btn btn-success btn-sm">
                          <i class="bx bx-home-circle"></i> Start Visit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Household List -->
                <div>
                  <h6 class="mb-3">Nearby Households</h6>
                  <div class="list-group">
                    <div v-for="household in nearbyHouseholds" :key="household.household_id"
                         class="list-group-item list-group-item-action"
                         :class="{'active': selectedHousehold?.household_id === household.household_id}"
                         @click="selectHousehold(household)">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 class="mb-1">{{ household.household_number }}</h6>
                          <p class="mb-0 small">{{ household.household_head_name || 'No head' }}</p>
                        </div>
                        <div>
                          <span v-if="household.cbhi_member_count > 0"
                                class="badge bg-success">CBHI</span>
                          <span v-else class="badge bg-secondary">Non-CBHI</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="me-auto">
            <small class="text-muted">
              <i class="bx bx-info-circle"></i>
              Click on markers to view household details
            </small>
          </div>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
  name: "HouseholdMapView",
  props: {
    households: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      markerCluster: null,
      selectedHousehold: null,
      nearbyHouseholds: [],

      // Filters
      filterType: 'all',
      filterVisit: 'all',
      visibleHouseholds: [],

      // Default center (Ethiopia)
      defaultCenter: [9.0320, 38.7469],
      defaultZoom: 13
    };
  },

  computed: {
    cbhiCount() {
      return this.visibleHouseholds.filter(h => h.cbhi_member_count > 0).length;
    },

    modelCount() {
      return this.visibleHouseholds.filter(h => h.is_model_household).length;
    },

    overdueCount() {
      return this.visibleHouseholds.filter(h => {
        if (!h.last_visit_date) return true;
        const daysSince = this.getDaysSinceVisit(h.last_visit_date);
        return daysSince > 30;
      }).length;
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initializeMap();
      this.loadHouseholds();
    });
  },

  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
  },

  methods: {
    initializeMap() {
      // Initialize the map
      this.map = L.map('household-map').setView(this.defaultCenter, this.defaultZoom);

      // Add tile layer (OpenStreetMap)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(this.map);

      // Add zoom control
      L.control.zoom({
        position: 'topright'
      }).addTo(this.map);

      // Add scale
      L.control.scale().addTo(this.map);
    },

    loadHouseholds() {
      this.clearMarkers();
      this.visibleHouseholds = this.filterHouseholds();

      // Create markers for households with GPS coordinates
      this.visibleHouseholds.forEach(household => {
        if (household.gps_latitude && household.gps_longitude) {
          const marker = this.createMarker(household);
          this.markers.push(marker);
          marker.addTo(this.map);
        }
      });

      // Fit map to show all markers
      if (this.markers.length > 0) {
        const group = new L.featureGroup(this.markers);
        this.map.fitBounds(group.getBounds().pad(0.1));
      }

      // Update nearby households list
      this.updateNearbyHouseholds();
    },

    createMarker(household) {
      const markerColor = this.getMarkerColor(household);
      const icon = this.createCustomIcon(markerColor);

      const marker = L.marker([household.gps_latitude, household.gps_longitude], { icon });

      // Create popup content
      const popupContent = `
        <div style="min-width: 200px;">
          <h6>${household.household_number}</h6>
          <p class="mb-1"><strong>Head:</strong> ${household.household_head_name || 'Not assigned'}</p>
          <p class="mb-1"><strong>Members:</strong> ${household.member_count || 0}</p>
          <p class="mb-1"><strong>CBHI:</strong> ${household.cbhi_member_count > 0 ? 'Yes' : 'No'}</p>
          ${household.is_model_household ? '<span class="badge bg-warning">Model Household</span>' : ''}
          ${household.is_vulnerable ? '<span class="badge bg-danger">Vulnerable</span>' : ''}
        </div>
      `;

      marker.bindPopup(popupContent);

      // Handle marker click
      marker.on('click', () => {
        this.selectHousehold(household);
      });

      return marker;
    },

    createCustomIcon(color) {
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: ${color}; width: 25px; height: 25px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
        iconSize: [25, 25],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12]
      });
    },

    getMarkerColor(household) {
      if (household.is_model_household) {
        return '#ffc107'; // Warning (yellow)
      }

      // Check if needs visit (never visited or > 30 days)
      if (!household.last_visit_date) {
        return '#dc3545'; // Danger (red)
      }

      const daysSince = this.getDaysSinceVisit(household.last_visit_date);
      if (daysSince > 30) {
        return '#dc3545'; // Danger (red)
      }

      if (household.cbhi_member_count > 0) {
        return '#28a745'; // Success (green)
      }

      return '#6c757d'; // Secondary (gray)
    },

    filterHouseholds() {
      let filtered = [...this.households];

      // Filter by type
      if (this.filterType === 'cbhi') {
        filtered = filtered.filter(h => h.cbhi_member_count > 0);
      } else if (this.filterType === 'non-cbhi') {
        filtered = filtered.filter(h => h.cbhi_member_count === 0);
      } else if (this.filterType === 'model') {
        filtered = filtered.filter(h => h.is_model_household);
      } else if (this.filterType === 'vulnerable') {
        filtered = filtered.filter(h => h.is_vulnerable);
      }

      // Filter by visit status
      if (this.filterVisit === 'never') {
        filtered = filtered.filter(h => !h.last_visit_date);
      } else if (this.filterVisit === 'overdue') {
        filtered = filtered.filter(h => {
          if (!h.last_visit_date) return true;
          return this.getDaysSinceVisit(h.last_visit_date) > 30;
        });
      } else if (this.filterVisit === 'recent') {
        filtered = filtered.filter(h => {
          if (!h.last_visit_date) return false;
          return this.getDaysSinceVisit(h.last_visit_date) <= 30;
        });
      }

      return filtered;
    },

    applyFilters() {
      this.loadHouseholds();
    },

    clearMarkers() {
      this.markers.forEach(marker => {
        marker.remove();
      });
      this.markers = [];
    },

    selectHousehold(household) {
      this.selectedHousehold = household;

      // Center map on selected household
      if (household.gps_latitude && household.gps_longitude) {
        this.map.setView([household.gps_latitude, household.gps_longitude], 17);

        // Open popup for the selected marker
        const marker = this.markers.find(m => {
          const pos = m.getLatLng();
          return pos.lat === household.gps_latitude && pos.lng === household.gps_longitude;
        });

        if (marker) {
          marker.openPopup();
        }
      }
    },

    updateNearbyHouseholds() {
      // For now, just show first 10 visible households
      // In a real app, you'd calculate actual distance
      this.nearbyHouseholds = this.visibleHouseholds.slice(0, 10);
    },

    viewHouseholdDetails() {
      if (this.selectedHousehold) {
        this.$emit('select', this.selectedHousehold);
        this.$emit('close');
      }
    },

    startVisit() {
      if (this.selectedHousehold) {
        // This would trigger the start visit modal
        this.$emit('start-visit', this.selectedHousehold);
        this.$emit('close');
      }
    },

    getDaysSinceVisit(dateString) {
      if (!dateString) return 0;
      const visitDate = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - visitDate);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

#household-map {
  z-index: 1;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

::v-deep .leaflet-popup-content {
  margin: 10px;
}

::v-deep .custom-marker {
  background: transparent;
  border: none;
}
</style>

<style>
/* Global styles for Leaflet */
.leaflet-container {
  font-family: inherit;
}

.leaflet-popup-content-wrapper {
  border-radius: 8px;
}

.leaflet-popup-content p {
  margin: 0;
}
</style>